import { QueryClient, MutationCache, type DefaultOptions } from "react-query";

import { trackPermissionDenied } from "src/lib/monitoring";

declare module "react-query" {
  interface QueryMeta {
    isFastEndpoint?: boolean;
    isAdmin?: boolean;
    usePermissionsLoader?: boolean;
  }
}

const mutationCache = new MutationCache({});

export const queryClient = new QueryClient({ mutationCache });

const initialQueryInvalidators = ["update_workspace"];

const preservedQueryKeys = [
  "Initial",
  "PermissionsV2",
  "GetWorkspaceEntitlements",
  "Workspaces",
  // Calendar setup checks read Snowflake and call the email provider; they rerun on their own schedule.
  "CampaignCalendarEngagementSetup",
  "CampaignCalendarDestinationPermissions",
];

export const invalidateQueries = () => {
  // small optimization to not invalidate initial query unless required
  // Returns a promise that resolves once active queries have refetched, so
  // callers can wait for fresh data before rendering from the cache.
  return queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      !preservedQueryKeys.includes(queryKey[0] as string),
  });
};

const options: DefaultOptions = {
  queries: {
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
    refetchOnWindowFocus: false,
    onError: () => {
      // handle all graphql errors with global toast and reporting
    },
  },
  mutations: {
    onError: () => {
      // handle all graphql errors with global toast and reporting
    },
    onSuccess: (data) => {
      if (queryClient) {
        const mutationKey =
          data && typeof data === "object" ? Object.keys(data)[0] : undefined;

        const mutationReturn =
          mutationKey && data && typeof data === "object"
            ? data[mutationKey]
            : undefined;
        if (mutationReturn === null) {
          trackPermissionDenied({
            source: "hasura_null",
            operation: mutationKey,
          });
        }
        // every mutation except will invalidate all active queries
        // to override this behavior pass `onSuccess` to the mutation hook.
        if (
          mutationKey &&
          initialQueryInvalidators.some((key) => mutationKey.includes(key))
        ) {
          queryClient.invalidateQueries();
        } else {
          invalidateQueries();
        }
      }
    },
  },
};

queryClient.setDefaultOptions(options);
